import { useMemo } from 'react';
import { X } from 'lucide-react';
import { ColumnDef, ColumnId, DateFilter, DateFilterState, FilterState } from './types';

interface ActiveFiltersBarProps<T> {
  columns: ColumnDef<T>[];
  filters: FilterState;
  dateFilters: DateFilterState;
  onClearColumn: (columnId: ColumnId) => void;
  onClearAll: () => void;
}

interface ActiveChip {
  columnId: ColumnId;
  label: string;
  summary: string;
}

const DATE_MODE_PREFIX: Record<string, string> = {
  eq: 'On',
  neq: 'Not on',
  gt: 'After',
  lt: 'Before',
  gte: 'On or after',
  lte: 'On or before',
  between: 'Between',
};

function summarizeDate(df: DateFilter): string {
  const prefix = DATE_MODE_PREFIX[df.mode] ?? df.mode;
  if (df.mode === 'between' && df.value2) {
    return `${prefix} ${df.value} – ${df.value2}`;
  }
  return `${prefix} ${df.value}`;
}

export function ActiveFiltersBar<T>({
  columns,
  filters,
  dateFilters,
  onClearColumn,
  onClearAll,
}: ActiveFiltersBarProps<T>) {
  const chips = useMemo<ActiveChip[]>(() => {
    const colById = new Map(columns.map((c) => [c.id, c]));
    const out: ActiveChip[] = [];

    for (const [colId, values] of Object.entries(filters)) {
      if (!values || values.size === 0) continue;
      const col = colById.get(colId);
      if (!col) continue;
      const arr = Array.from(values);
      const summary =
        arr.length <= 2 ? arr.join(', ') : `${arr.length} selected`;
      out.push({ columnId: colId, label: col.label, summary });
    }

    for (const [colId, df] of Object.entries(dateFilters)) {
      if (!df) continue;
      const col = colById.get(colId);
      if (!col) continue;
      out.push({ columnId: colId, label: col.label, summary: summarizeDate(df) });
    }

    return out;
  }, [columns, filters, dateFilters]);

  if (chips.length === 0) return null;

  return (
    <div className="flex items-center flex-wrap gap-1.5 px-1 pb-2">
      <span className="text-xs font-medium text-stone-500 mr-1">Filtered by:</span>
      {chips.map((chip) => (
        <button
          key={chip.columnId}
          onClick={() => onClearColumn(chip.columnId)}
          className="group inline-flex items-center gap-1 h-6 pl-2 pr-1 rounded-sm bg-[#0078d4]/10 text-[#0078d4] text-xs font-medium hover:bg-[#0078d4]/15 transition-colors"
          title={`Clear ${chip.label} filter`}
        >
          <span className="max-w-[180px] truncate">
            <span className="font-semibold">{chip.label}:</span>{' '}
            <span className="font-normal">{chip.summary}</span>
          </span>
          <X className="w-3 h-3 shrink-0 opacity-70 group-hover:opacity-100" />
        </button>
      ))}
      <button
        onClick={onClearAll}
        className="text-xs font-medium text-stone-600 hover:text-[#0078d4] ml-1 px-1.5 h-6"
      >
        Clear all
      </button>
    </div>
  );
}
